import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { PopButton } from "../PopButton";
import { local } from "../../lib/storage";
import type { Category } from "../../data/categories";
import type { LandingPage } from "../../data/landingPages";

const KEY = "housemates";

export function useHousemates(): [string[], (v: string[]) => void] {
  const [names, setState] = useState<string[]>(() => local.customOptions(KEY));
  const set = (v: string[]) => { setState(v); local.setCustomOptions(KEY, v); };
  return [names, set];
}

interface Props { page: LandingPage; cat: Category; names: string[]; setNames: (v: string[]) => void; choreToo: boolean; setChoreToo: (v: boolean) => void; onSpin: () => void }

export function NamesSpinBox({ page, cat, names, setNames, choreToo, setChoreToo, onSpin }: Props) {
  const [text, setText] = useState("");
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (names.some((n) => n.toLowerCase() === v.toLowerCase())) return toast.error("They're already on the wheel.");
    if (names.length >= 12) return toast.error("12 names max — that's a big house!");
    setNames([...names, v]);
    setText("");
  };
  return (
    <div className="rounded-[20px] border-[3px] border-[#FFC15E] bg-[#2A0F45] p-4 text-white" data-testid="landing-spin-box">
      <p className="ua-title text-2xl sm:text-3xl">{cat.question}</p>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input className="field !text-[#3b1800]" maxLength={30} placeholder="Add a housemate, e.g. Sam" value={text} onChange={(e) => setText(e.target.value)} data-testid="housemate-input" />
        <PopButton type="submit" tone="yellow" size="sm" aria-label="Add housemate" data-testid="housemate-add-button"><Plus className="h-5 w-5" strokeWidth={3} /></PopButton>
      </form>
      <div className="mt-2 flex min-h-[32px] flex-wrap gap-1.5" data-testid="housemate-list">
        {names.map((n, i) => (
          <span key={n} className="inline-flex max-w-full items-center gap-1 rounded-full border-2 border-[#3b1800] bg-[#FFE14D] px-3 py-0.5 font-comic text-sm text-[#3b1800]" data-testid={`housemate-${i}`}>
            <span className="truncate">{n}</span>
            <button aria-label={`Remove ${n}`} onClick={() => setNames(names.filter((x) => x !== n))} data-testid={`housemate-remove-${i}`}><X className="h-3.5 w-3.5" strokeWidth={3} /></button>
          </span>
        ))}
        {!names.length && <span className="text-xs font-bold text-white/60">No one on the wheel yet.</span>}
      </div>
      <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-bold">
        <input type="checkbox" className="h-5 w-5 accent-[#FFE14D]" checked={choreToo} onChange={(e) => setChoreToo(e.target.checked)} data-testid="chore-too-checkbox" />
        Pick the chore too
      </label>
      <PopButton tone={cat.tone} size="xl" className="mt-3 w-full" disabled={names.length < 2} onClick={onSpin} data-testid="landing-spin-button">{page.spinLabel}</PopButton>
      <p className="mt-2 text-center text-xs font-bold text-white/70" data-testid="landing-pool-count">
        {names.length < 2 ? "Add at least 2 names to spin." : `Picking from ${names.length} people${choreToo ? ` and ${cat.items.length} chores` : ""}`}
      </p>
    </div>
  );
}
